import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { Botao, Esqueleto } from '@/componentes/ui'
import { PaginaAdmin } from '@/funcionalidades/admin/PaginaAdmin'
import { PaginaEntrar } from '@/funcionalidades/auth/PaginaEntrar'
import { PaginaBiblioteca } from '@/funcionalidades/biblioteca/PaginaBiblioteca'
import { PaginaComunidade } from '@/funcionalidades/comunidade/PaginaComunidade'
import { PaginaDiario } from '@/funcionalidades/diario/PaginaDiario'
import { PaginaEvolucao } from '@/funcionalidades/evolucao/PaginaEvolucao'
import { PaginaInicio } from '@/funcionalidades/inicio/PaginaInicio'
import { PaginaInstalar } from '@/funcionalidades/instalacao/PaginaInstalar'
import { PaginaOnboarding } from '@/funcionalidades/onboarding/PaginaOnboarding'
import { PaginaPerfil } from '@/funcionalidades/perfil/PaginaPerfil'
import { diasParaComecar, formatarData } from '@/lib/datas'
import { detectarPlataforma, estaInstalado } from '@/lib/instalacao'
import { pedirArmazenamentoPersistente } from '@/lib/offline'
import { registrarAssinatura } from '@/lib/push'
import {
  CHAVES,
  type ContextoPrograma,
  useContextoPrograma,
  useMatricula,
  useMatriculaEncerrada,
  usePerfil,
} from '@/lib/sessao'
import { observarConexao, sincronizarDiarios } from '@/lib/sincronizacao'
import { supabase } from '@/lib/supabase'
import type { RodaVidaArea } from '@/tipos/tabelas'
import { BarraNavegacao, type Aba } from './BarraNavegacao'

/**
 * Portões de entrada do app, em ordem:
 *
 *   instalada? → autenticada? → onboarding feito? → app
 *
 * A instalação vem primeiro porque no iOS ela é pré-requisito técnico
 * para o Web Push, não uma preferência de experiência.
 */
export function App() {
  const [instalada, setInstalada] = useState(() => estaInstalado())
  const [sessaoId, setSessaoId] = useState<string | null | undefined>(undefined)
  const [aba, setAba] = useState<Aba>('inicio')
  const [naAdmin, setNaAdmin] = useState(() => window.location.pathname.startsWith('/admin'))
  const clienteQuery = useQueryClient()

  // No desktop não bloqueamos: é onde a administração trabalha, e onde
  // a participante às vezes abre pela primeira vez antes de ir ao celular.
  // A administração também nunca passa pelo portão de instalação — é uma
  // ferramenta de trabalho, não a PWA da participante.
  const exigeInstalacao = !naAdmin && detectarPlataforma() !== 'desktop' && !instalada

  function irPara(caminho: string, admin: boolean) {
    window.history.pushState({}, '', caminho)
    setNaAdmin(admin)
  }

  // Botão voltar do navegador
  useEffect(() => {
    const aoNavegar = () => setNaAdmin(window.location.pathname.startsWith('/admin'))
    window.addEventListener('popstate', aoNavegar)
    return () => window.removeEventListener('popstate', aoNavegar)
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSessaoId(data.session?.user.id ?? null))

    const { data: assinatura } = supabase.auth.onAuthStateChange((_evento, sessao) => {
      setSessaoId(sessao?.user.id ?? null)
      void clienteQuery.invalidateQueries()
    })

    return () => assinatura.subscription.unsubscribe()
  }, [clienteQuery])

  // Rotinas de abertura: reinscrever no push (o iOS invalida assinaturas
  // em silêncio), pedir armazenamento persistente e esvaziar a fila.
  useEffect(() => {
    if (!sessaoId) return

    void registrarAssinatura()
    void pedirArmazenamentoPersistente()
    void sincronizarDiarios()
    return observarConexao()
  }, [sessaoId])

  if (exigeInstalacao) {
    return <PaginaInstalar aoConcluir={() => setInstalada(true)} />
  }

  if (sessaoId === undefined) return <CarregandoApp />

  if (sessaoId === null) {
    return <PaginaEntrar aoEntrar={() => void clienteQuery.invalidateQueries()} />
  }

  if (naAdmin) {
    return <PaginaAdmin aoSair={() => irPara('/', false)} />
  }

  return (
    <AreaAutenticada
      userId={sessaoId}
      aba={aba}
      aoTrocarAba={setAba}
      aoAbrirAdmin={() => irPara('/admin', true)}
    />
  )
}

function AreaAutenticada({
  userId,
  aba,
  aoTrocarAba,
  aoAbrirAdmin,
}: {
  userId: string
  aba: Aba
  aoTrocarAba: (aba: Aba) => void
  aoAbrirAdmin: () => void
}) {
  const { data: matricula, isLoading } = useMatricula()
  const contexto = useContextoPrograma()
  const clienteQuery = useQueryClient()

  const { data: areasRodaVida = [] } = useQuery({
    queryKey: ['roda-vida-areas', matricula?.turma.programa.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roda_vida_areas')
        .select('*')
        .eq('programa_id', matricula?.turma.programa.id ?? '')
        .eq('ativa', true)
        .order('ordem')

      if (error) throw error
      return data as RodaVidaArea[]
    },
    enabled: Boolean(matricula) && !matricula?.onboarding_completo,
  })

  if (isLoading) return <CarregandoApp />

  if (!matricula) return <SemMatricula aoAbrirAdmin={aoAbrirAdmin} />

  if (!matricula.onboarding_completo) {
    return (
      <PaginaOnboarding
        matriculaId={matricula.id}
        userId={userId}
        areasRodaVida={areasRodaVida}
        aoConcluir={() => void clienteQuery.invalidateQueries({ queryKey: CHAVES.matricula })}
      />
    )
  }

  if (!contexto) return <CarregandoApp />

  if (!contexto.iniciado) return <AguardandoInicio contexto={contexto} />

  return (
    <div className="mx-auto min-h-dvh max-w-md">
      {aba === 'inicio' && <PaginaInicio contexto={contexto} aoAbrirPilar={() => undefined} />}
      {aba === 'diario' && <PaginaDiario contexto={contexto} />}
      {aba === 'comunidade' && <PaginaComunidade contexto={contexto} />}
      {aba === 'biblioteca' && <PaginaBiblioteca contexto={contexto} />}
      {aba === 'evolucao' && <PaginaEvolucao contexto={contexto} />}
      {aba === 'perfil' && <PaginaPerfil contexto={contexto} />}

      <BarraNavegacao ativa={aba} aoTrocar={aoTrocarAba} />
    </div>
  )
}

/**
 * Tela de espera para quem já se matriculou numa turma com início no
 * futuro — comum quando a Raquel cadastra a turma com alguns dias de
 * antecedência. Sem isto, `dia` ficaria negativo e a tela inicial teria
 * que tratar esse caso sozinha; mais simples bloquear aqui, no mesmo
 * lugar que já bloqueia por onboarding incompleto.
 */
function AguardandoInicio({ contexto }: { contexto: ContextoPrograma }) {
  const { turma } = contexto.matricula
  const faltam = diasParaComecar(turma.data_inicio)

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
      <span className="text-5xl" aria-hidden>
        🌱
      </span>
      <h1 className="font-titulo text-xl font-bold">Quase lá</h1>
      <p className="text-[15px] leading-relaxed text-[var(--tinta-suave)]">
        A {turma.nome} começa{' '}
        <strong>
          {formatarData(turma.data_inicio, "d 'de' MMMM")}
          {faltam === 1 ? ' — amanhã' : faltam > 1 ? ` — daqui a ${faltam} dias` : ''}
        </strong>
        . Volte nesse dia para começar o Dia 1.
      </p>
    </main>
  )
}

function CarregandoApp() {
  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 px-5 pt-10">
      <Esqueleto className="h-8 w-2/3" />
      <Esqueleto className="h-4 w-1/2" />
      <Esqueleto className="h-3 w-full" />
      <div className="mt-4 grid grid-cols-5 gap-2">
        {[0, 1, 2, 3, 4].map((n) => (
          <Esqueleto key={n} className="h-20" />
        ))}
      </div>
      <Esqueleto className="mt-4 h-64" />
    </div>
  )
}

function SemMatricula({ aoAbrirAdmin }: { aoAbrirAdmin: () => void }) {
  const { data: perfil } = usePerfil()
  const { data: encerrada, isLoading } = useMatriculaEncerrada()

  if (isLoading) return <CarregandoApp />

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
      <span className="text-5xl" aria-hidden>
        {encerrada ? '🎉' : '🌱'}
      </span>
      <h1 className="font-titulo text-xl font-bold">
        {encerrada ? 'Sua turma terminou' : 'Você ainda não está em uma turma'}
      </h1>
      <p className="text-[15px] leading-relaxed text-[var(--tinta-suave)]">
        {encerrada ? (
          <>
            Você concluiu os 28 dias {encerrada.turma?.nome ? `da ${encerrada.turma.nome}` : ''}.
            Os lembretes foram desligados. Para continuar, fale com a equipe do Projeto Viver e
            peça para ser inscrita numa nova turma.
          </>
        ) : (
          'Assim que sua matrícula for liberada, o programa aparece aqui. Se você já se inscreveu, fale com a equipe do Projeto Viver.'
        )}
      </p>

      {/* A administradora também não tem matrícula: sem esta porta ela
          ficaria presa nesta tela sem alcançar o painel. */}
      {perfil?.papel === 'admin' && (
        <Botao variante="contorno" tamanho="pequeno" className="mt-3" onClick={aoAbrirAdmin}>
          Abrir a administração
        </Botao>
      )}
    </main>
  )
}

